/**
 * SIS bundle types. Used by the bundler Lambda, API, frontend, and CDK stacks.
 *
 * A "SIS bundle" is one Canvas SIS Imports POST that ships whichever PeopleSoft
 * SIS files n8n dropped (normally all 5 of accounts, courses, enrollments,
 * sections, users, plus the optional logins file) and — when validation
 * passes — the worker's ghost-fix CSV. One row per drop in
 * DynamoDB `CanvasSyncSisBundles`, one S3 archive subprefix at
 * `sis-archive/{env}/{dropId}/`.
 *
 * Replaces the legacy Jenkins ship-script which globbed *.csv from a
 * Jenkins outbox and ran `zip + curl` against the same SIS Imports endpoint.
 * The lifecycle, validation, and contradiction-checking logic is new; the
 * wire format (`POST /api/v1/accounts/{id}/sis_imports?import_type=instructure_csv`,
 * multipart `attachment` field carrying a zip) matches legacy byte-for-byte.
 */

import type { CanvasEnv } from "./runs.js";
import type { SisBundleSubmissionMode } from "./settings.js";

/**
 * The 5 PS SIS files we expect on every drop. A drop missing some of them
 * still ships whatever it has; the missing ones are recorded on the bundle
 * (`missingFiles`) as a warning. Filenames are stable on
 * psfiles (see `tools/n8n/canvas-sync-sis-bundle.json`) and are preserved
 * verbatim inside the Canvas zip — Canvas detects file types by inspecting
 * the header row of each CSV, not by filename, but keeping the PS names
 * makes archive forensics readable.
 */
export const SIS_BUNDLE_FILES = [
  "SJDC_SR_CANVAS_ACCOUNTS.csv",
  "SJDC_SR_CANVAS_COURSES.csv",
  "SJDC_SR_CANVAS_ENROLLMENTS.csv",
  "SJDC_SR_CANVAS_SECTIONS.csv",
  "SJDC_SR_CANVAS_USERS.csv",
] as const;

export type SisBundleFile = (typeof SIS_BUNDLE_FILES)[number];

/**
 * PS SIS files n8n may also drop. Their absence is never a warning, but when
 * the manifest lists one it's hash-checked, archived, and zipped to Canvas
 * like the 5 expected files.
 */
export const SIS_BUNDLE_OPTIONAL_FILES = ["SJDC_SR_CANVAS_LOGINS.csv"] as const;

export type SisBundleOptionalFile = (typeof SIS_BUNDLE_OPTIONAL_FILES)[number];

/** Every PS SIS file a manifest can list: expected first, then optional. */
export const SIS_BUNDLE_INPUT_FILES = [
  ...SIS_BUNDLE_FILES,
  ...SIS_BUNDLE_OPTIONAL_FILES,
] as const;

export type SisBundleInputFile = (typeof SIS_BUNDLE_INPUT_FILES)[number];

/** The expected SIS files a drop's file list doesn't include, in canonical order. */
export function missingSisBundleFiles(
  fileNames: readonly string[],
): SisBundleFile[] {
  return SIS_BUNDLE_FILES.filter((name) => !fileNames.includes(name));
}

/** The fix CSV always uses this name inside the bundle zip. */
export const SIS_BUNDLE_FIX_FILE = "SJDC_SR_CANVAS_FIX.csv";

/**
 * Bundle lifecycle. Non-terminal statuses are progressed by the bundler
 * Lambda; terminal statuses are the resting state for dashboards / alerts.
 *
 * Terminal: INCOMPLETE_INPUTS, INVALID_INPUTS, SKIPPED_GHOST_DETECTION_TIMEOUT,
 *           DRY_RUN_BUILT, SKIPPED_UNCHANGED, SKIPPED_NO_FIX, SUBMITTED,
 *           SUBMITTED_SIS_ONLY, SUBMITTED_FIX_ONLY, FAILED.
 *
 * GHOST_FIX_ONLY-mode terminals (see `SisBundleSubmissionMode`):
 *   - SUBMITTED_FIX_ONLY — the dual of SUBMITTED_SIS_ONLY: the ghost-fix CSV
 *     was POSTed to Canvas WITHOUT the 5 SIS files.
 *   - SKIPPED_NO_FIX — fix-only mode had nothing safe to send (no ghosts, no
 *     fix generated, no enrollments file, contradiction, drift, or timeout —
 *     see fixSkipReason), so NOTHING was POSTed.
 *
 * Alert-on (see alerts policy):
 *   - INCOMPLETE_INPUTS, INVALID_INPUTS, SKIPPED_GHOST_DETECTION_TIMEOUT,
 *     FAILED, SUBMITTED_SIS_ONLY / SKIPPED_NO_FIX (only when fixSkipReason ∈
 *     {CONTRADICTION, INPUT_DRIFT}).
 */
export const SIS_BUNDLE_STATUSES = [
  "RECEIVED",
  "VALIDATING",
  "INCOMPLETE_INPUTS",
  "INVALID_INPUTS",
  "WAITING_FOR_GHOST_DETECTION",
  "BUNDLING",
  "POSTING",
  "DRY_RUN_BUILT",
  "SKIPPED_UNCHANGED",
  "SKIPPED_GHOST_DETECTION_TIMEOUT",
  "SKIPPED_NO_FIX",
  "SUBMITTED",
  "SUBMITTED_SIS_ONLY",
  "SUBMITTED_FIX_ONLY",
  "FAILED",
] as const;

export type SisBundleStatus = (typeof SIS_BUNDLE_STATUSES)[number];

export const SIS_BUNDLE_TERMINAL_STATUSES: SisBundleStatus[] = [
  "INCOMPLETE_INPUTS",
  "INVALID_INPUTS",
  "SKIPPED_GHOST_DETECTION_TIMEOUT",
  "DRY_RUN_BUILT",
  "SKIPPED_UNCHANGED",
  "SKIPPED_NO_FIX",
  "SUBMITTED",
  "SUBMITTED_SIS_ONLY",
  "SUBMITTED_FIX_ONLY",
  "FAILED",
];

export function isSisBundleTerminal(status: SisBundleStatus): boolean {
  return SIS_BUNDLE_TERMINAL_STATUSES.includes(status);
}

/**
 * Resolve a bundle's frozen submission mode, defaulting legacy rows (created
 * before the setting existed) to FULL_BUNDLE — the behavior they were created
 * under.
 */
export function sisBundleSubmissionModeOf(bundle: {
  submissionMode?: SisBundleSubmissionMode;
}): SisBundleSubmissionMode {
  return bundle.submissionMode ?? "FULL_BUNDLE";
}

/**
 * Why the fix file did not ride along in the bundle. NO_GHOSTS_FOUND and
 * NO_FIX_GENERATED are happy paths (no alert). NO_ENROLLMENTS_FILE means the
 * drop had no enrollments file to run ghost detection against; the missing
 * file is already flagged by `missingFiles`. CONTRADICTION, INPUT_DRIFT,
 * and GHOST_DETECTION_TIMEOUT are problem states (alert + dashboard).
 */
export const FIX_SKIP_REASONS = [
  "NO_GHOSTS_FOUND",
  "NO_FIX_GENERATED",
  "NO_ENROLLMENTS_FILE",
  "CONTRADICTION",
  "INPUT_DRIFT",
  "GHOST_DETECTION_TIMEOUT",
] as const;

export type FixSkipReason = (typeof FIX_SKIP_REASONS)[number];

export const SIS_BUNDLE_TRIGGERS = ["n8n-cron", "manual-retrigger"] as const;
export type SisBundleTrigger = (typeof SIS_BUNDLE_TRIGGERS)[number];

/**
 * One entry in the manifest's `files[]` array — what n8n wrote at upload time.
 */
export interface ManifestFileEntry {
  name: SisBundleInputFile;
  /** Full S3 key under the app bucket. */
  s3Key: string;
  bytes: number;
  /** Hex md5 computed locally by n8n before upload. */
  md5: string;
  /** mtime of the file on psfiles at read time. */
  psModifiedAt: string;
}

/**
 * `_MANIFEST.json` — written LAST by n8n to `sis-inputs/{env}/{dropId}/`.
 * The S3 ObjectCreated event on this object triggers the bundler. By the
 * time it lands, every listed SIS file already exists, and so does the
 * ghost-detection input copy at `ghostDetection.enrollmentsS3Key` when
 * enrollments is one of them. `files[]` lists only the files n8n found.
 */
export interface SisBundleManifest {
  schemaVersion: 1;
  env: CanvasEnv;
  dropId: string;
  createdAt: string;
  source: {
    host: string;
    path: string;
    n8nWorkflowId?: string;
    n8nExecutionId?: string;
  };
  files: ManifestFileEntry[];
  /** md5 of `sortedFileMd5sByName.join("|")` — audit only, not a skip key. */
  combinedSisBundleHash: string;
  /** Ignored when `files[]` has no enrollments entry. */
  ghostDetection: {
    /** Where n8n uploaded the enrollments copy that feeds the worker. */
    enrollmentsS3Key: string;
    /** Same md5 as the matching entry in `files[]`. */
    enrollmentsMd5: string;
  };
  /**
   * Set ONLY when this manifest was written by the resubmit API (never
   * by n8n). Tells the bundler to:
   *   - tag the new bundle row as triggeredBy="manual-retrigger" +
   *     sourceBundleId
   *   - skip the ghost-detection run lookup entirely (no matching run
   *     exists for an archive-based replay) and instead reconstruct the
   *     fix decision from the source bundle row
   *   - optionally bypass the skip-unchanged check
   *
   * The manifest's `files[].s3Key` and `ghostDetection.enrollmentsS3Key`
   * point to the SOURCE bundle's archive (immutable) so re-validation
   * still works without depending on the original `sis-inputs/` keys.
   */
  retrigger?: {
    sourceBundleId: string;
    /**
     * When true, the bundler ignores any matching lastSuccessfulPayloadHash
     * and POSTs even if the resubmit produces identical bytes. Use when
     * the operator KNOWS Canvas-side state diverged and wants to re-apply.
     */
    bypassSkipUnchanged?: boolean;
  };
}

/**
 * Output of the validation phase. Pure: no DDB writes, just a read of S3.
 */
export interface SisBundleValidationResult {
  /** WARN = some expected SIS files are missing; the bundle still ships. */
  status: "PASS" | "WARN" | "FAIL";
  /** Set when status=FAIL — drives the terminal bundle status. */
  terminalStatus?: "INCOMPLETE_INPUTS" | "INVALID_INPUTS";
  reason?: string;
  filesPresent: boolean;
  hashesMatch: boolean;
  /** s3:// URI of the archived validation.json artifact. */
  artifactS3Uri: string;
}

/**
 * Output of the "should the fix file ride along?" decision. Carries the
 * fix CSV bytes when include=true so the bundler doesn't re-read S3.
 */
export interface SisFixDecision {
  include: boolean;
  skipReason?: FixSkipReason;
  /** s3:// URI of the worker-generated fix CSV (set when a matching run exists). */
  fixS3Uri?: string;
  /** Hex md5 of the fix CSV bytes. Required when include=true. */
  fixMd5?: string;
  /**
   * Number of fix rows that contradicted Active Student rows in the bundled
   * SIS enrollments file. Any value > 0 ⇒ include=false, skipReason=CONTRADICTION.
   */
  contradictionCount?: number;
  /** s3:// URI of the archived contradicting-rows sample (first N rows). */
  contradictionSampleS3Uri?: string;
}

/**
 * Canonical DynamoDB record for one SIS bundle drop.
 *
 * Partition key:  PK     = `BUNDLE#${env}#${dropId}`
 * Sort key:       SK     = `META`
 * GSI1:           GSI1PK = `STATUS#${status}`, GSI1SK = `${createdAt}`
 * GSI2:           GSI2PK = `ENV#${env}`,       GSI2SK = `${createdAt}`
 */
export interface CanvasSyncSisBundle {
  /** `{env}#{dropId}`. Derived. URL-safe (`#` becomes `%23` in URLs). */
  bundleId: string;
  env: CanvasEnv;
  /** `YYYY-MM-DDTHH-MM-SS` (UTC). Set by n8n at pull time, propagated via manifest. */
  dropId: string;
  triggeredBy: SisBundleTrigger;
  /** Set when this row is a manual-retrigger of an earlier bundle. */
  sourceBundleId?: string;

  // ─── lifecycle ────────────────────────────────────────────────────────
  status: SisBundleStatus;
  createdAt: string;
  updatedAt: string;
  submittedAt?: string;
  errorMessage?: string;
  /** Number of times the bundler has deferred on WAITING_FOR_GHOST_DETECTION. */
  deferCount: number;
  lastDeferAt?: string;

  // ─── inputs ──────────────────────────────────────────────────────────
  manifestS3Uri: string;
  sourcePath: string;
  expectedFiles: SisBundleFile[];
  /**
   * Expected SIS files the drop didn't include. The bundle ships without
   * them; absent when nothing was missing.
   */
  missingFiles?: SisBundleFile[];
  /** Filename → hex md5, copied from the manifest at validation time. */
  fileHashes: Record<string, string>;
  combinedSisBundleHash: string;

  // ─── ghost-detection linkage ─────────────────────────────────────────
  ghostDetectionInputS3Key: string;
  ghostDetectionEnrollmentsMd5: string;
  /** Set when validation resolves the matching worker run. */
  ghostDetectionRunId?: string;
  /** Result of the byte-identical check between the two enrollments copies. */
  enrollmentsBytesMatch?: boolean;

  // ─── fix file ────────────────────────────────────────────────────────
  fixFileS3Uri?: string;
  /**
   * Hex md5 of the fix CSV bytes. Set when fixIncluded=true. Used by
   * the resubmit path so the new bundle can compute finalPayloadHash
   * without re-reading the fix file from S3.
   */
  fixFileMd5?: string;
  fixIncluded: boolean;
  fixSkipReason?: FixSkipReason;
  contradictionCount?: number;
  contradictionSampleS3Uri?: string;
  /**
   * Set on bundles created by the resubmit API. When true, the bundler
   * ignores the skip-unchanged check and POSTs even if finalPayloadHash
   * matches the last successful POST for this env.
   */
  bypassSkipUnchanged?: boolean;

  // ─── validation ──────────────────────────────────────────────────────
  validationStatus: "PASS" | "WARN" | "FAIL";
  validationArtifactS3Uri: string;

  // ─── dry-run + target ────────────────────────────────────────────────
  dryRun: boolean;
  /** Snapshot of the prod safety guard state at row creation. Forensic-only. */
  productionSafetyGuardActive: boolean;
  /**
   * What this bundle ships to Canvas, FROZEN from the operator setting
   * (`AppSettings.sisBundleSubmissionMode`) at row-creation time — same
   * freeze semantics as `dryRun`. Absent on rows created before the setting
   * existed; readers must treat absence as "FULL_BUNDLE" (use
   * `sisBundleSubmissionModeOf`).
   */
  submissionMode?: SisBundleSubmissionMode;
  canvasTarget: {
    domain: string;
    accountId: string;
    env: CanvasEnv;
  };

  // ─── skip-unchanged calculation ──────────────────────────────────────
  /**
   * md5 of the exact-payload identity string — combines every manifest SIS
   * file md5, the fix file md5 (or `NOFIX=${reason}`), import_type, batch_mode,
   * and the Canvas target (env+domain+accountId). See
   * `services/api/src/handlers/common/sisBundleSubmit.ts:computeFinalPayloadHash`.
   */
  finalPayloadHash?: string;
  /**
   * Copied from the most recent SUBMITTED or SUBMITTED_SIS_ONLY bundle for
   * the same env at decision time. If it matches finalPayloadHash, the
   * bundler short-circuits to SKIPPED_UNCHANGED without POSTing.
   */
  lastSuccessfulPayloadHash?: string;

  // ─── Canvas POST outcome (synchronous response at POST time) ─────────
  sisImportId?: number;
  /**
   * Canvas's `workflow_state`. Initially the value returned by the POST
   * (usually "created"); subsequently advanced by `refreshSisBundleFromCanvas`
   * as Canvas processes the import (importing → imported / failed / …).
   */
  canvasWorkflowState?: string;
  canvasResponseS3Uri?: string;
  submittedZipS3Uri?: string;
  submittedZipBytes?: number;

  // ─── Canvas processing result (reconciled on read after the POST) ────
  // Populated by `refreshSisBundleFromCanvas`, which polls the SIS import by
  // id exactly the way `refreshUploadFromCanvas` does for standalone uploads.
  // Absent until the first reconcile observes Canvas-side progress, so the UI
  // must treat every field here as optional. Only meaningful for bundles that
  // actually POSTed (SUBMITTED / SUBMITTED_SIS_ONLY / SUBMITTED_FIX_ONLY).
  /** Canvas's reported per-resource counts (e.g. `enrollments_deleted`). */
  canvasCounts?: Record<string, number>;
  canvasWarnings?: number;
  canvasErrors?: number;
  /** Small sample of Canvas `processing_errors` lines (capped). */
  canvasProcessingErrorsSample?: string[];
  /** Small sample of Canvas `processing_warnings` lines (capped). */
  canvasProcessingWarningsSample?: string[];
  /** When Canvas began / finished processing the import. */
  canvasStartedAt?: string;
  canvasEndedAt?: string;
  /** submittedAt → canvasEndedAt, computed once both are known. */
  canvasProcessingDurationMs?: number;
  /** Bounded audit of workflow_state transitions seen while polling. */
  canvasPollSamples?: { at: string; workflowState: string }[];

  // ─── row counts (computed at bundle build time) ──────────────────────
  /**
   * Data-row count (excludes the header) per file name, including the fix
   * file when it shipped. Computed by `buildAndSubmitSisBundle` while it reads
   * each CSV to assemble the zip — so it reflects exactly what was sent.
   * Absent on bundles that never built a zip (e.g. SKIPPED_UNCHANGED).
   */
  fileRowCounts?: Record<string, number>;
  /** Sum of `fileRowCounts` across every file shipped to Canvas. */
  totalRowsSubmitted?: number;

  // ─── archive ─────────────────────────────────────────────────────────
  /** s3:// prefix where all artifacts for this bundle live. */
  archivePrefix: string;
}

/**
 * Lightweight projection for the dashboard list view.
 */
export interface SisBundleSummary {
  bundleId: string;
  env: CanvasEnv;
  dropId: string;
  status: SisBundleStatus;
  triggeredBy: SisBundleTrigger;
  fixIncluded: boolean;
  fixSkipReason?: FixSkipReason;
  contradictionCount?: number;
  /** Expected SIS files the drop didn't include (see `CanvasSyncSisBundle`). */
  missingFiles?: SisBundleFile[];
  sisImportId?: number;
  /** Run whose fix CSV this bundle shipped (or waited on). Lets the unified
   *  Canvas-submissions feed link a bundle row back to its sync. */
  ghostDetectionRunId?: string;
  /** Total CSV rows in the submitted zip; absent pre-submit and on dry-runs. */
  totalRowsSubmitted?: number;
  /**
   * Canvas processing outcome, surfaced in the list so a Canvas-side failure
   * isn't masked by the terminal lifecycle status — `SUBMITTED_SIS_ONLY` means
   * "we shipped it", NOT "Canvas accepted it". Mirrors the same fields on
   * `CanvasSyncSisBundle`; kept fresh because `sisBundles/list` reconciles each
   * posted bundle against Canvas (same self-heal as `uploads/list`).
   */
  canvasWorkflowState?: string;
  canvasWarnings?: number;
  canvasErrors?: number;
  dryRun: boolean;
  /** Frozen submission mode; absent on legacy rows (= FULL_BUNDLE). */
  submissionMode?: SisBundleSubmissionMode;
  createdAt: string;
  updatedAt: string;
  submittedAt?: string;
}

/**
 * Archived `result.json` shape. Lives at
 * `sis-archive/{env}/{dropId}/result.json`. Captured at terminal-status
 * transition. The DDB row is the searchable index; this is the immutable
 * record co-located with the zip bytes for incident response.
 */
export interface SisBundleResultArchive {
  bundleId: string;
  status: SisBundleStatus;
  finalPayloadHash?: string;
  submittedAt?: string;
  sisImportId?: number;
  canvasWorkflowState?: string;
  submittedZipBytes?: number;
  fixIncluded: boolean;
  fixSkipReason?: FixSkipReason;
  contradictionCount?: number;
  dryRun: boolean;
  /** Frozen submission mode; absent on archives written before the setting. */
  submissionMode?: SisBundleSubmissionMode;
  canvasTarget: {
    domain: string;
    accountId: string;
    env: CanvasEnv;
  };
}

// ─── API request/response shapes ─────────────────────────────────────────

export interface ListSisBundlesRequest {
  env?: CanvasEnv;
  status?: SisBundleStatus;
  limit?: number;
  cursor?: string;
}

export interface ListSisBundlesResponse {
  bundles: SisBundleSummary[];
  nextCursor?: string;
}

export type GetSisBundleResponse = CanvasSyncSisBundle;

/** Server reads everything else it needs from the source bundle's archive. */
export interface ResubmitSisBundleRequest {
  /** Optional override — by default the new bundle targets the source's env. */
  canvasEnv?: CanvasEnv;
  /**
   * When true, the bundler ignores any matching lastSuccessfulPayloadHash and
   * POSTs even if the resubmit produces identical bytes. Surfaces as the
   * manifest's `retrigger.bypassSkipUnchanged`. Use when the operator KNOWS
   * Canvas-side state diverged and wants to re-apply.
   */
  forceResubmit?: boolean;
}

export interface ResubmitSisBundleResponse {
  bundleId: string;
  status: SisBundleStatus;
  sourceBundleId: string;
}

/**
 * Names of artifacts archived at `sis-archive/{env}/{dropId}/`. Used by the
 * artifact-presign endpoint to validate the requested artifact and map to
 * the right S3 key.
 */
export const SIS_BUNDLE_ARTIFACT_NAMES = [
  "manifest",
  "fix",
  "zip",
  "result",
  "validation",
  "canvas-response",
  "contradiction-sample",
] as const;

export type SisBundleArtifactName = (typeof SIS_BUNDLE_ARTIFACT_NAMES)[number];
